/* ==========================================================================
   Color swatches via Storefront API  (Krago international)
   --------------------------------------------------------------------------
   Renders the PDP color swatches client-side instead of in Liquid, so we are
   not capped by Liquid's `shop.metaobjects.product_groups.values` page limit.

   Data model (Krago):
     metaobject  type=product_groups   (one entry per color family)
       └─ field "products" → list of Product refs
            Product
              └─ metafield custom.color_swatch → COLOR_SWATCH metaobject
                   ├─ field "label" → single line text (color name)
                   ├─ field "color" → color (hex fallback)
                   └─ field "image" → File(image) (optional, overrides color)

   Emits the SAME markup the old Liquid block produced, so assets/custom.css
   (.color-swatches / .color-swatch / .color-swatch__inner) styles it unchanged.
   ========================================================================== */
(function () {
  'use strict';
  console.debug('[swatches] script loaded');

  // --- Storefront API config -----------------------------------------------
  // Public Storefront token: needs unauthenticated_read_metaobjects +
  // unauthenticated_read_product_listings. Read-only & safe to expose.
  // To rotate: replace token and bump CACHE_KEY version suffix.
  var STOREFRONT_ENDPOINT =
    'https://kragoacc.myshopify.com/api/2025-10/graphql.json';
  var STOREFRONT_TOKEN = 'a4b2f2d7c4fde682e45d581e4f59ee8a'; // public Storefront token
  // Swatch labels are translated metaobject fields: ask for the page's language.
  var LANGUAGE = (document.documentElement.lang || 'cs').split('-')[0].toUpperCase();
  var CACHE_KEY = 'krago-swatches-v3-' + LANGUAGE;
  var MAX_PAGES = 50; // guard: 50 x 250 = 12,500 groups

  var QUERY = [
    'query KragoSwatchGroups($pageSize: Int = 250, $after: String, $language: LanguageCode) @inContext(language: $language) {',
    '  metaobjects(type: "product_groups", first: 250, after: $after) {',
    '    pageInfo { hasNextPage endCursor }',
    '    nodes {',
    '      handle',
    '      products: field(key: "products") {',
    '        references(first: $pageSize) {',
    '          nodes { ...ProductWithSwatch }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}',
    'fragment ProductWithSwatch on Product {',
    '  handle',
    '  onlineStoreUrl',
    '  featuredImage { url }',
    '  swatch: metafield(namespace: "custom", key: "color_swatch") {',
    '    reference {',
    '      ... on Metaobject {',
    '        label: field(key: "label") { value }',
    '        color: field(key: "color") { value }',
    '        image: field(key: "image") {',
    '          reference { ... on MediaImage { image { url } } }',
    '        }',
    '      }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  // --- Fetch + cache --------------------------------------------------------
  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
  }

  function writeCache(groups) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(groups));
    } catch (e) {
      /* sessionStorage full / unavailable — non-fatal */
    }
  }

  function normalizeGroup(node) {
    var refs = (node.products && node.products.references &&
      node.products.references.nodes) || [];
    var products = refs.map(function (p) {
      var ref = p.swatch && p.swatch.reference;
      var img = ref && ref.image && ref.image.reference &&
        ref.image.reference.image;
      return {
        handle: p.handle,
        url: p.onlineStoreUrl || null,
        label: (ref && ref.label && ref.label.value) || '',
        color: (ref && ref.color && ref.color.value) || '',
        image: (img && img.url) || '',
      };
    });
    return { handle: node.handle, products: products };
  }

  var inflight = null;
  function fetchGroups() {
    var cached = readCache();
    if (cached) return Promise.resolve(cached);
    if (inflight) return inflight;

    inflight = (function () {
      var all = [];
      var after = null;

      function page(pageNum) {
        if (pageNum > MAX_PAGES) {
          console.warn('[swatches] hit MAX_PAGES cap, truncating groups');
          return Promise.resolve(all);
        }
        return fetch(STOREFRONT_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
          },
          body: JSON.stringify({
            query: QUERY,
            variables: { pageSize: 250, after: after, language: LANGUAGE },
          }),
        })
          .then(function (r) { return r.json(); })
          .then(function (json) {
            var conn = json && json.data && json.data.metaobjects;
            if (!conn) {
              console.warn('[swatches] no metaobjects payload', json && json.errors);
              return all;
            }
            (conn.nodes || []).forEach(function (n) {
              all.push(normalizeGroup(n));
            });
            if (conn.pageInfo && conn.pageInfo.hasNextPage) {
              after = conn.pageInfo.endCursor;
              return page(pageNum + 1);
            }
            return all;
          });
      }

      return page(1).then(function (groups) {
        if (groups && groups.length) writeCache(groups); // never cache empty
        return groups;
      });
    })();

    return inflight;
  }

  // --- Rendering ------------------------------------------------------------
  function buildUrl(product, rootUrl) {
    // Prefer a root_url-relative path so the current locale/market is kept
    // (onlineStoreUrl is the canonical, locale-less primary-domain URL).
    var base = (rootUrl || '/').replace(/\/$/, '');
    return base + '/products/' + product.handle;
  }

  function render(el, group, currentHandle, rootUrl, colorLabel) {
    var active = null;
    for (var i = 0; i < group.products.length; i++) {
      if (group.products[i].handle === currentHandle) {
        active = group.products[i];
        break;
      }
    }

    var list = document.createElement('div');
    list.className = 'color-swatches__list';

    group.products.forEach(function (p) {
      // Match Liquid: skip products without a swatch (no image AND no color)
      if (!p.image && !p.color) return;

      var a = document.createElement('a');
      a.href = buildUrl(p, rootUrl);
      a.className = 'color-swatch' +
        (p.handle === currentHandle ? ' color-swatch--active' : '');
      a.setAttribute('data-label', p.label);
      a.setAttribute('aria-label', p.label);
      if (p.handle === currentHandle) a.setAttribute('aria-current', 'page');

      var inner = document.createElement('span');
      inner.className = 'color-swatch__inner';
      if (p.image) {
        inner.style.backgroundImage = "url('" + p.image + "')";
        inner.style.backgroundSize = 'cover';
        inner.style.backgroundPosition = 'center';
      } else {
        inner.style.backgroundColor = p.color;
      }
      a.appendChild(inner);
      list.appendChild(a);
    });

    if (!list.children.length) return false; // nothing usable → keep fallback

    var heading = document.createElement('div');
    heading.className = 'color-swatches__heading';
    var key = document.createElement('span');
    key.className = 'color-swatches__key';
    key.textContent = colorLabel + ':';
    var val = document.createElement('span');
    val.className = 'color-swatches__value';
    val.textContent = active ? active.label : '';
    heading.appendChild(key);
    heading.appendChild(val);

    el.innerHTML = '';
    el.appendChild(heading);
    el.appendChild(list);
    return true;
  }

  // --- Web component --------------------------------------------------------
  function ColorSwatchesSf() {
    return Reflect.construct(HTMLElement, [], ColorSwatchesSf);
  }
  ColorSwatchesSf.prototype = Object.create(HTMLElement.prototype);
  ColorSwatchesSf.prototype.constructor = ColorSwatchesSf;

  ColorSwatchesSf.prototype.connectedCallback = function () {
    var el = this;
    console.debug('[swatches] connected', el.getAttribute('data-product-handle'));
    var currentHandle = el.getAttribute('data-product-handle');
    var rootUrl = el.getAttribute('data-root-url') || '/';
    var colorLabel = el.getAttribute('data-color-label') || 'Color';
    if (!currentHandle) return;
    if (STOREFRONT_TOKEN.indexOf('__') === 0) return; // token not set → fallback

    // NOTE: any pre-rendered children are the Liquid fallback. We only wipe
    // them when the Storefront API actually yields swatches; on empty / error
    // we leave the server-rendered fallback in place.
    fetchGroups()
      .then(function (groups) {
        if (!groups || !groups.length) return; // empty → keep fallback
        var group = null;
        for (var i = 0; i < groups.length; i++) {
          var found = groups[i].products.some(function (p) {
            return p.handle === currentHandle;
          });
          if (found) { group = groups[i]; break; }
        }
        if (!group) return; // not in any group → keep fallback
        render(el, group, currentHandle, rootUrl, colorLabel);
      })
      .catch(function (e) {
        console.warn('[swatches] failed to load, keeping fallback', e);
      });
  };

  if (!customElements.get('color-swatches-sf')) {
    customElements.define('color-swatches-sf', ColorSwatchesSf);
  }
})();
